/**
 * Agent login for preview environments (FANL-1664).
 *
 * Claude (and any other browser automation) will not type a password into a
 * login form on a non-local host, and Microsoft SSO does not work on `pr-*`
 * subdomains, so every preview-env check used to stall until a human signed
 * in. `apps/pocketbase/bin/preview-login.py` now mints a short-lived token for
 * a seeded dev user through the superuser impersonation API and prints
 *
 *   https://pr-<nr>.dev.froygruppen.no/<path>#agent_token=<jwt>
 *
 * Opening that URL signs the browser in: this module reads the token from the
 * fragment, checks it against /app/me, stores the session and strips the
 * fragment before the router ever sees it.
 *
 * The token travels in the fragment rather than the query string because the
 * browser never sends a fragment to the server, so it stays out of ingress and
 * PocketBase request logs.
 *
 * Guards, all of which must hold:
 * - the runtime config opts in (`agentLogin`, set only by preview-engage.sh);
 * - the page is served from a `pr-<nr>.dev.froygruppen.no` preview or
 *   localhost, so a mis-set flag still cannot turn it on in staging or prod;
 * - the token is an unexpired auth token for the `users` collection.
 *
 * Minting a token still needs the superuser credentials, so none of this lets
 * anyone in who could not already impersonate a user.
 */
import type { Data } from "./data";

export const AGENT_TOKEN_PARAM = "agent_token";

const PREVIEW_HOST = /^pr-\d+\.dev\.froygruppen\.no$/;
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1"]);

export function isAgentLoginHost(hostname: string): boolean {
  return PREVIEW_HOST.test(hostname) || LOCAL_HOSTS.has(hostname);
}

/** The token in a `#agent_token=<jwt>` fragment, or undefined. */
export function readAgentToken(hash: string): string | undefined {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const token = params.get(AGENT_TOKEN_PARAM)?.trim();
  return token || undefined;
}

/** The fragment with the agent token removed, keeping anything else in it. */
export function stripAgentToken(hash: string): string {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  if (!params.has(AGENT_TOKEN_PARAM)) return hash;
  params.delete(AGENT_TOKEN_PARAM);
  const rest = params.toString();
  return rest ? `#${rest}` : "";
}

type AuthClaims = { id: string; collectionId: string; type: string; exp: number };

/**
 * The claims of a PocketBase auth JWT, or undefined if it is not one. This is
 * a shape check only; the signature is the server's business, and /app/me is
 * asked before the session is kept.
 */
export function decodeAuthClaims(token: string): AuthClaims | undefined {
  const parts = token.split(".");
  if (parts.length !== 3) return undefined;
  try {
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as Partial<AuthClaims>;
    if (
      typeof claims.id !== "string" ||
      typeof claims.collectionId !== "string" ||
      claims.type !== "auth" ||
      typeof claims.exp !== "number"
    ) {
      return undefined;
    }
    return claims as AuthClaims;
  } catch {
    return undefined;
  }
}

export type AgentLoginVerdict =
  | { kind: "none" }
  | { kind: "refused"; reason: string }
  | { kind: "accept"; token: string; claims: AuthClaims };

/** Decides what to do with the current URL, without touching any state. */
export function evaluateAgentLogin(opts: {
  enabled: boolean;
  hostname: string;
  hash: string;
  nowSeconds: number;
}): AgentLoginVerdict {
  const token = readAgentToken(opts.hash);
  if (!token) return { kind: "none" };
  if (!opts.enabled) return { kind: "refused", reason: "agentLogin is off in config.json" };
  if (!isAgentLoginHost(opts.hostname))
    return { kind: "refused", reason: `host ${opts.hostname} is not a preview env` };
  const claims = decodeAuthClaims(token);
  if (!claims) return { kind: "refused", reason: "not a PocketBase auth token" };
  if (claims.exp <= opts.nowSeconds) return { kind: "refused", reason: "token has expired" };
  return { kind: "accept", token, claims };
}

/**
 * Signs the browser in from a `#agent_token=` fragment, if there is one and
 * every guard holds. Always strips the token from the address bar, accepted or
 * not, so it never lingers in history or gets copied along with the URL.
 */
export async function consumeAgentLogin(enabled: boolean): Promise<void> {
  const verdict = evaluateAgentLogin({
    enabled,
    hostname: location.hostname,
    hash: location.hash,
    nowSeconds: Date.now() / 1000,
  });
  if (verdict.kind === "none") return;

  history.replaceState(history.state, "", location.pathname + location.search + stripAgentToken(location.hash));

  if (verdict.kind === "refused") {
    console.warn("Agent login refused:", verdict.reason);
    return;
  }

  // Imported lazily so the pure helpers above stay testable without a
  // runtime config (api.ts awaits /config.json at module load).
  const [{ authStore }, { MeApi }] = await Promise.all([import("./auth-store"), import("./api/me")]);
  authStore.save(verdict.token, {
    id: verdict.claims.id,
    collectionId: verdict.claims.collectionId,
    collectionName: "users",
  });
  try {
    const user = await MeApi.fetchMe<Data.User>();
    if (!user) throw new Error("token does not belong to a portal user");
    authStore.save(verdict.token, user as unknown as Parameters<typeof authStore.save>[1]);
    console.info("Agent login: signed in as", user.email);
  } catch (error) {
    console.warn("Agent login failed, staying signed out:", error);
    authStore.clear();
  }
}
